import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { watchMyRequests } from '@/services/firebase/requests';
import { toUserMessage } from '@/utils/errors';
import type { GuestRequest } from '@/types';
import { newNotices, noticeMillis, unreadNoticeCount } from '@/features/requests/notices';

/**
 * The guest's requests, subscribed once for the whole app, and the
 * notifications that fall out of them.
 *
 * A guest is an anonymous browser session: no push token, no phone number.
 * So "notify the guest" means: the server writes `guestNotification` on the
 * request when staff change its status or confirm the charge, this listener
 * sees the change, and the page raises a toast — plus a browser Notification
 * when the tab is in the background and the guest has allowed them. The
 * Requests tab carries a badge for notices the guest has not looked at.
 *
 * Only *changes* notify. The first snapshot after a reload is the baseline,
 * or every open request would re-announce itself on each visit.
 */

interface RequestsContextValue {
  requests: GuestRequest[] | null;
  error: string | null;
  retry: () => void;
  /** Requests with a notice newer than the last visit to /requests. */
  unreadCount: number;
}

const RequestsContext = createContext<RequestsContextValue | null>(null);

const SEEN_KEY = 'hotel-voice:requests-seen';

/**
 * "When did the guest last look at the Requests page" — a tiny external store
 * over localStorage, so the badge survives a reload and the page can mark
 * things seen from an effect (updating an external system) rather than by
 * setting React state inside one.
 */
const seenStore = (() => {
  let listeners: Array<() => void> = [];
  const read = (): number => {
    try {
      return Number(window.localStorage.getItem(SEEN_KEY) ?? 0) || 0;
    } catch {
      return 0;
    }
  };
  let value = read();
  return {
    get: () => value,
    set: (next: number) => {
      value = next;
      try {
        window.localStorage.setItem(SEEN_KEY, String(next));
      } catch {
        // Private browsing can refuse storage; the badge just persists a bit longer.
      }
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener: () => void) => {
      listeners.push(listener);
      return () => {
        listeners = listeners.filter((entry) => entry !== listener);
      };
    },
  };
})();

export function RequestsProvider({ children }: { children: ReactNode }) {
  const { location, uid } = useReadyGuest();
  const routerLocation = useLocation();
  const [attempt, setAttempt] = useState(0);
  const [feed, setFeed] = useState<{
    attempt: number;
    requests: GuestRequest[] | null;
    error: string | null;
  } | null>(null);
  const seenAt = useSyncExternalStore(seenStore.subscribe, seenStore.get);
  const lastNotice = useRef<Map<string, number> | null>(null);

  useEffect(() => {
    lastNotice.current = null;
    return watchMyRequests(
      location.hotelId,
      uid,
      (next) => {
        announceChanges(lastNotice, next);
        setFeed({ attempt, requests: next, error: null });
      },
      (err) => setFeed({ attempt, requests: null, error: toUserMessage(err) }),
    );
  }, [location.hotelId, uid, attempt]);

  // Visiting the Requests page marks everything as seen.
  const onRequestsPage = routerLocation.pathname === '/requests';
  useEffect(() => {
    if (onRequestsPage) seenStore.set(Date.now());
  }, [onRequestsPage, feed]);

  const settled = feed !== null && feed.attempt === attempt;
  const requests = settled ? feed.requests : null;
  const error = settled ? feed.error : null;
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const unreadCount = useMemo(() => unreadNoticeCount(requests, seenAt), [requests, seenAt]);

  const value = useMemo(
    () => ({ requests, error, retry, unreadCount }),
    [requests, error, retry, unreadCount],
  );

  return <RequestsContext.Provider value={value}>{children}</RequestsContext.Provider>;
}

/** Raises a toast (and a background Notification) for every request whose notice is new since the last snapshot. */
function announceChanges(
  lastNotice: React.MutableRefObject<Map<string, number> | null>,
  next: GuestRequest[],
): void {
  const previous = lastNotice.current;
  lastNotice.current = new Map(next.map((request) => [request.id, noticeMillis(request)]));

  for (const request of newNotices(previous, next)) {
    const notice = request.guestNotification!;
    toast(notice.title, { description: notice.body, duration: 8000 });

    if (
      document.visibilityState === 'hidden' &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      try {
        new Notification(notice.title, { body: notice.body, tag: request.id });
      } catch {
        // Some browsers refuse `new Notification` outside a service worker; the toast still ran.
      }
    }
  }
}

/** Asks for browser notifications once, after the guest has done something worth notifying about. */
export async function requestNotificationPermission(): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  try {
    await Notification.requestPermission();
  } catch {
    // Denied or unsupported: toasts still work while the page is open.
  }
}

export function useMyRequests(): RequestsContextValue {
  const value = useContext(RequestsContext);
  if (!value) throw new Error('useMyRequests must be used inside RequestsProvider');
  return value;
}
